import * as React from "react";

import { llmApi, type ProvidersResponse } from "@/api/llm";
import {
  settingsApi,
  type MeProfile,
  type UserSettings,
} from "@/api/settings";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useUIStore } from "@/stores/ui";

export function PersonalSettings(): JSX.Element {
  const [settings, setSettings] = React.useState<UserSettings | null>(null);
  const [profile, setProfile] = React.useState<MeProfile | null>(null);
  const [providers, setProviders] = React.useState<ProvidersResponse | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [displayName, setDisplayName] = React.useState("");
  const [savingName, setSavingName] = React.useState(false);
  const pushToast = useUIStore((s) => s.pushToast);

  React.useEffect(() => {
    void (async () => {
      try {
        const [s, p, prof] = await Promise.all([
          settingsApi.getMine(),
          llmApi.listProviders(),
          settingsApi.getProfile(),
        ]);
        setSettings(s);
        setProviders(p);
        setProfile(prof);
        setDisplayName(prof.display_name ?? "");
      } catch (e) {
        pushToast({
          variant: "destructive",
          title: "Lỗi",
          description: e instanceof Error ? e.message : String(e),
        });
      }
    })();
  }, [pushToast]);

  const saveDisplayName = async (): Promise<void> => {
    if (!profile) return;
    const trimmed = displayName.trim();
    if (trimmed === (profile.display_name ?? "")) return;
    setSavingName(true);
    try {
      const updated = await settingsApi.updateProfile({
        display_name: trimmed || null,
      });
      setProfile(updated);
      pushToast({ variant: "success", title: "Đã lưu tên hiển thị" });
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lưu thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSavingName(false);
    }
  };

  const update = async (patch: Partial<UserSettings>): Promise<void> => {
    if (!settings) return;
    setSaving(true);
    try {
      const updated = await settingsApi.updateMine(patch);
      setSettings(updated);
      pushToast({ variant: "success", title: "Đã lưu" });
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lưu thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return <div className="text-sm text-muted-foreground">Đang tải...</div>;
  }

  const availableProviders = (providers?.providers ?? []).filter((p) => p.available);
  const selectedProvider = settings.default_llm_provider
    ? availableProviders.find((p) => p.id === settings.default_llm_provider)
    : null;

  return (
    <div className="max-w-2xl space-y-6">
      <header>
        <h1 className="text-xl font-semibold">Cài đặt cá nhân</h1>
        <p className="text-sm text-muted-foreground">
          Thay đổi tuỳ biến chỉ áp dụng cho tài khoản của bạn.
        </p>
      </header>

      {/* Doc 48: Profile section — display name + identity info */}
      {profile ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Hồ sơ</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <Input value={profile.email} disabled />
              <p className="text-xs text-muted-foreground mt-1">
                Email không thể thay đổi. Liên hệ admin nếu cần.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Tên hiển thị
              </label>
              <div className="flex gap-2">
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="VD: Nguyễn Văn A"
                  maxLength={255}
                  disabled={savingName}
                  className="flex-1"
                />
                <Button
                  onClick={() => void saveDisplayName()}
                  disabled={
                    savingName || displayName.trim() === (profile.display_name ?? "")
                  }
                >
                  {savingName ? "Đang lưu..." : "Lưu"}
                </Button>
              </div>
            </div>
            <div className="text-xs text-muted-foreground space-y-0.5">
              <div>
                <span className="font-medium">Native password:</span>{" "}
                {profile.has_password ? "✓ Đã thiết lập" : "Chưa có (chỉ Odoo)"}
              </div>
              <div>
                <span className="font-medium">Odoo identity:</span>{" "}
                {profile.has_odoo_identity
                  ? `✓ ${profile.odoo_db} #${profile.odoo_user_id}`
                  : "Chưa map"}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Giao diện</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Theme</label>
            <div className="flex gap-2">
              {(["light", "dark", "auto"] as const).map((t) => (
                <Button
                  key={t}
                  variant={settings.theme === t ? "default" : "outline"}
                  size="sm"
                  disabled={saving}
                  onClick={() => void update({ theme: t })}
                >
                  {t === "light" ? "Sáng" : t === "dark" ? "Tối" : "Tự động"}
                </Button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Ngôn ngữ</label>
            <div className="flex gap-2">
              {(["vi", "en"] as const).map((l) => (
                <Button
                  key={l}
                  variant={settings.locale === l ? "default" : "outline"}
                  size="sm"
                  disabled={saving}
                  onClick={() => void update({ locale: l })}
                >
                  {l === "vi" ? "Tiếng Việt" : "English"}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">LLM mặc định</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Chọn provider/model để dùng mặc định khi tạo hội thoại mới. Để trống
            sẽ dùng cấu hình tenant.
          </p>

          <div>
            <label className="block text-sm font-medium mb-1">Provider</label>
            <select
              className="w-full h-9 rounded-md border bg-background px-3 text-sm"
              value={settings.default_llm_provider ?? ""}
              disabled={saving}
              onChange={(e) =>
                void update({
                  default_llm_provider: e.target.value || null,
                  default_llm_model: null,
                })
              }
            >
              <option value="">— Mặc định tenant —</option>
              {availableProviders.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          {selectedProvider ? (
            <div>
              <label className="block text-sm font-medium mb-1">Model</label>
              <select
                className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                value={settings.default_llm_model ?? ""}
                disabled={saving}
                onChange={(e) =>
                  void update({ default_llm_model: e.target.value || null })
                }
              >
                <option value="">— Auto —</option>
                {selectedProvider.models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                    {m.is_default ? " (khuyến nghị)" : ""}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
